import {
  AllColumns,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  FlowHistoricalMonthlyColumns,
  FlowObservationColumns,
  NDSIHistoricalColumns,
  NDSIObservationColumns,
  NDVIHistoricalColumns,
  NDVIObservationColumns,
  RFCColumns,
  SWEHistoricalColumns,
  SWEObservationColumns,
  Source,
  WeatherColumns,
  WeatherHistoricalMonthlyColumns,
} from './api/series';
import COLORS, {getHfColors, getRFCColors} from './colors';

export const PRECIP_FILL_OPACITY = 0.1;

// The short-term weather chart's source order, which fixes each source's color and legend
// position; the H5 weather chart follows it so the two sections match.
export const WEATHER_SOURCES = [
  Source.ECMWFHRES,
  Source.GFS,
  Source.GEFS,
  Source.HRRR,
  Source.WEATHERNEXT_2,
];

export enum ColumnType {
  OBSERVED = 'observed',
  FORECAST = 'forecast',
}

type ColumnConfigType = {
  legendDisplay: string;
  tooltipDisplay: string;
  style: (
    idx?: number,
    numForecasts?: number
  ) => React.SVGProps<SVGRectElement> & React.SVGProps<SVGLineElement>;
};

type SourceColumnsConfigType = {
  [ColumnType.OBSERVED]?: ColumnConfigType;
  [ColumnType.FORECAST]?: ColumnConfigType;
};

const FlowColumnConfig = {
  [ColumnType.OBSERVED]: {
    legendDisplay: '',
    tooltipDisplay: '',
    style: () => ({stroke: COLORS.OBS, strokeWidth: 1.5}),
  },
} as SourceColumnsConfigType;

export const FlowSourceConfig = {
  display: 'Observed',
  columns: {
    [FlowObservationColumns.DISCHARGE]: FlowColumnConfig,
    [FlowObservationColumns.DISCHARGE_RAW]: FlowColumnConfig,
    [FlowObservationColumns.DWR_FLOW]: FlowColumnConfig,
    [FlowObservationColumns.FLOW]: FlowColumnConfig,
    [FlowObservationColumns.HAH_FLOW]: FlowColumnConfig,
    [FlowObservationColumns.HGH_FLOW]: FlowColumnConfig,
    [FlowObservationColumns.LIB_FLOW]: FlowColumnConfig,
    [FlowObservationColumns.INFLOW]: FlowColumnConfig,
    [FlowObservationColumns.OUTFLOW]: FlowColumnConfig,
    [FlowObservationColumns.NATURAL_FLOW]: FlowColumnConfig,
  },
};

// IGNORE until we can tear this out
// @ts-ignore
export const ConfigBySourceId: {
  [source in Source]: {
    display: string;
    displayLong?: string;
    columns: {
      [column in AllColumns]?: SourceColumnsConfigType;
    };
  };
} = {
  [Source.FLOW_HISTORICAL_DAILY]: {
    display: 'Historical',
    columns: {
      [FlowHistoricalDailyColumns.FLOW_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.OBS, strokeDasharray: 2}),
        },
      },
    },
  },
  [Source.FLOW_HISTORICAL_MONTHLY]: {
    display: 'Historical',
    columns: {
      [FlowHistoricalMonthlyColumns.FLOW_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: () => ({
            stroke: COLORS.OBS,
            strokeDasharray: 3,
          }),
        },
      },
    },
  },
  [Source.USGS]: FlowSourceConfig,
  [Source.PRIVATE_GAUGE_DATA]: FlowSourceConfig,
  [Source.NET_DISCHARGE]: FlowSourceConfig,
  [Source.CO_DWR]: FlowSourceConfig,
  [Source.CA_DWR]: FlowSourceConfig,
  [Source.USACE]: FlowSourceConfig,
  [Source.CEHQ_GAUGE]: FlowSourceConfig,
  [Source.PACIFICORP_GAUGE]: FlowSourceConfig,
  [Source.AUCKLAND_GAUGE]: FlowSourceConfig,
  [Source.PGE_GAUGE]: FlowSourceConfig,
  [Source.EXTERNAL_GAUGE_DATA]: FlowSourceConfig,
  [Source.COMPOUND_GAUGE_DATA]: FlowSourceConfig,
  [Source.HydroForecastShortTerm]: {
    display: 'HydroForecast',
    displayLong: 'HydroForecast Short Term',
    columns: {
      [FlowForecastColumns.DISCHARGE_0_05]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI min',
          style: () => ({
            fill: COLORS.HF_90,
            fillOpacity: 0.3,
          }),
        },
      },

      [FlowForecastColumns.DISCHARGE_0_25]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '50% CI',
          tooltipDisplay: '50% CI min',
          style: () => ({fill: COLORS.HF_50, fillOpacity: 0.6}),
        },
      },
      [FlowForecastColumns.DISCHARGE_MEDIAN]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'median',
          tooltipDisplay: 'median',
          style: (idx = 0, numForecasts = 0) => {
            let strokeColor;
            if (numForecasts === 0) {
              const hfColors = getHfColors(0);
              strokeColor = hfColors[hfColors.length - 1];
            } else {
              const hfColors = getHfColors(numForecasts);
              strokeColor = hfColors[idx];
            }
            return {stroke: strokeColor};
          },
        },
      },
      [FlowForecastColumns.DISCHARGE_MEAN]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: (idx = 0, numForecasts = 0) => {
            let strokeColor;
            if (numForecasts === 0) {
              const hfColors = getHfColors(0);
              strokeColor = hfColors[hfColors.length - 1];
            } else {
              const hfColors = getHfColors(numForecasts);
              strokeColor = hfColors[idx];
            }
            return {stroke: strokeColor};
          },
        },
      },
      [FlowForecastColumns.DISCHARGE_0_75]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '50% CI',
          tooltipDisplay: '50% CI max',
          style: () => ({fill: COLORS.HF_50, fillOpacity: 0.6}),
        },
      },
      [FlowForecastColumns.DISCHARGE_0_95]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI max',
          style: () => ({
            fill: COLORS.HF_90,
            fillOpacity: 0.3,
          }),
        },
      },
    },
  },
  [Source.RFC]: {
    display: 'RFC',
    columns: {
      [RFCColumns.FLOW]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'flow',
          tooltipDisplay: 'flow',
          style: (idx = 0, numForecasts = 0) => {
            let strokeColor;
            if (numForecasts === 0) {
              const rfcColors = getRFCColors(0);
              strokeColor = rfcColors[rfcColors.length - 1];
            } else {
              const rfcColors = getRFCColors(numForecasts);
              strokeColor = rfcColors[idx];
            }
            return {stroke: strokeColor, strokeDasharray: 5};
          },
        },
      },
    },
  },
  [Source.HydroForecastSeasonal]: {
    display: 'HydroForecast',
    displayLong: 'HydroForecast Seasonal',
    columns: {
      [FlowForecastColumns.DISCHARGE_0_05]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI min',
          style: () => ({fill: COLORS.HF_90, stroke: COLORS.HF_90}),
        },
      },
      [FlowForecastColumns.DISCHARGE_0_25]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '50% CI',
          tooltipDisplay: '50% CI min',
          style: () => ({fill: COLORS.HF_50, fillOpacity: 0.6}),
        },
      },
      [FlowForecastColumns.DISCHARGE_MEDIAN]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'median',
          tooltipDisplay: 'median',
          style: () => ({stroke: COLORS.HF_MEAN}),
        },
      },
      [FlowForecastColumns.DISCHARGE_MEAN]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.HF_MEAN}),
        },
      },
      [FlowForecastColumns.DISCHARGE_0_75]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '50% CI',
          tooltipDisplay: '50% CI max',
          style: () => ({fill: COLORS.HF_50, fillOpacity: 0.6}),
        },
      },
      [FlowForecastColumns.DISCHARGE_0_95]: {
        [ColumnType.FORECAST]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI max',
          style: () => ({fill: COLORS.HF_90, stroke: COLORS.HF_90}),
        },
      },
    },
  },
  [Source.ECMWFHRES]: {
    display: 'ECMWF',
    columns: {
      [WeatherColumns.PRECIPITATION]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.WEATHER_1,
            fill: COLORS.WEATHER_1,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
      },
      [WeatherColumns.TEMPERATURE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.OBS,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.WEATHER_1,
          }),
        },
      },
    },
  },
  [Source.WEATHERNEXT_2]: {
    display: 'WeatherNext 2',
    columns: {
      [WeatherColumns.PRECIPITATION]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.WEATHER_5,
            fill: COLORS.WEATHER_5,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
      },
      [WeatherColumns.TEMPERATURE]: {
        [ColumnType.FORECAST]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.WEATHER_5,
          }),
        },
      },
    },
  },
  [Source.GFS]: {
    display: 'GFS',
    columns: {
      [WeatherColumns.PRECIPITATION]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.WEATHER_2,
            fill: COLORS.WEATHER_2,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
      },
      [WeatherColumns.TEMPERATURE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.OBS,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.WEATHER_2,
          }),
        },
      },
    },
  },
  [Source.HRRR]: {
    display: 'HRRR',
    columns: {
      [WeatherColumns.PRECIPITATION]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.WEATHER_4,
            fill: COLORS.WEATHER_4,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
      },
      [WeatherColumns.TEMPERATURE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.OBS,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.WEATHER_4,
          }),
        },
      },
    },
  },
  [Source.GEFS]: {
    display: 'GEFS',
    columns: {
      [WeatherColumns.PRECIPITATION]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.OBS,
            fill: COLORS.OBS,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'precipitation',
          tooltipDisplay: 'precipitation',
          style: () => ({
            stroke: COLORS.WEATHER_3,
            fill: COLORS.WEATHER_3,
            fillOpacity: PRECIP_FILL_OPACITY,
          }),
        },
      },
      [WeatherColumns.TEMPERATURE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.OBS,
          }),
        },
        [ColumnType.FORECAST]: {
          legendDisplay: 'temperature',
          tooltipDisplay: 'temperature',
          style: () => ({
            stroke: COLORS.WEATHER_3,
          }),
        },
      },
    },
  },
  [Source.GEFS_HISTORICAL_MONTHLY]: {
    display: 'Historical',
    columns: {
      [WeatherHistoricalMonthlyColumns.PRECIPITATION_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.OBS, strokeDasharray: 3}),
        },
      },
      [WeatherHistoricalMonthlyColumns.TEMPERATURE_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: 'mean',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.OBS, strokeDasharray: 3}),
        },
      },
    },
  },

  [Source.MODIS_NDSI]: {
    display: 'MODIS NDSI',
    columns: {
      [NDSIObservationColumns.NDSI_MEDIAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: '',
          style: () => ({stroke: COLORS.OBS}),
        },
      },
    },
  },
  [Source.MODIS_NDSI_HISTORICAL_DAILY]: {
    display: 'Historical',
    columns: {
      [NDSIHistoricalColumns.NDSI_0_05]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI min',
          style: () => ({fill: COLORS.SNOW_RANGE}),
        },
      },
      [NDSIHistoricalColumns.NDSI_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.SNOW_LTA, strokeDasharray: 3}),
        },
      },
      [NDSIHistoricalColumns.NDSI_0_95]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI max',
          style: () => ({fill: COLORS.SNOW_RANGE}),
        },
      },
    },
  },
  [Source.MODIS_NDVI]: {
    display: 'MODIS NDVI',
    columns: {
      [NDVIObservationColumns.NDVI_MAX]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: '',
          style: () => ({stroke: COLORS.OBS}),
        },
      },
    },
  },
  [Source.MODIS_NDVI_HISTORICAL_DAILY]: {
    display: 'Historical',
    columns: {
      [NDVIHistoricalColumns.NDVI_0_05]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI min',
          style: () => ({fill: COLORS.VEG_RANGE}),
        },
      },
      [NDVIHistoricalColumns.NDVI_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.VEG_LTA, strokeDasharray: 3}),
        },
      },
      [NDVIHistoricalColumns.NDVI_0_95]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI max',
          style: () => ({fill: COLORS.VEG_RANGE}),
        },
      },
    },
  },
  [Source.SNODAS]: {
    display: 'SNODAS',
    columns: {
      [SWEObservationColumns.SWE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: '',
          style: () => ({stroke: COLORS.OBS}),
        },
      },
    },
  },
  [Source.SWANN]: {
    display: 'SWANN',
    columns: {
      [SWEObservationColumns.SWE]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: '',
          style: () => ({stroke: COLORS.SWANN_OBS}),
        },
      },
    },
  },
  [Source.SNODAS_HISTORICAL_DAILY]: {
    display: 'Historical',
    columns: {
      [SWEHistoricalColumns.SWE_0_05]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI min',
          style: () => ({fill: COLORS.SNOW_RANGE}),
        },
      },
      [SWEHistoricalColumns.SWE_MEAN]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '',
          tooltipDisplay: 'mean',
          style: () => ({stroke: COLORS.SNOW_LTA, strokeDasharray: 3}),
        },
      },
      [SWEHistoricalColumns.SWE_0_95]: {
        [ColumnType.OBSERVED]: {
          legendDisplay: '90% CI',
          tooltipDisplay: '90% CI max',
          style: () => ({fill: COLORS.SNOW_RANGE}),
        },
      },
    },
  },
};
